import React, { useState } from 'react';
import { Tag, Tooltip } from '@app/ui';
import { removePostFromCampaign } from '../api/postCampaigns';
import connect from '@app/utils/connect';
import combineActions from '@app/utils/combineActions';
import sendNotification from '@app/actions/notifications/send';
import * as Sentry from '@sentry/browser';

const MAX_CAMPAIGN_TAGS_SHOWN_IN_COLLAPSE = 2;

type Campaign = { id: number; name: string };
type ExpandableCampaignTagsProps = {
    client: { id: number };
    postId: number;
    campaigns: Campaign[];
    onRemoveCampaign: (id: number, name: string) => void;
    sendNotification: (
        message: string,
        config?: { context: 'success' | 'warning' | 'danger' },
    ) => void;
};

const ExpandableCampaignTags = ({
    client,
    postId,
    campaigns = [],
    onRemoveCampaign,
    sendNotification,
}: ExpandableCampaignTagsProps) => {
    const [collapsed, setCollapsed] = useState(true);
    const showCollapse =
        collapsed && campaigns.length > MAX_CAMPAIGN_TAGS_SHOWN_IN_COLLAPSE + 1;

    const handleRemove = (campaign: Campaign) => async () => {
        try {
            await removePostFromCampaign(client.id, postId, campaign.id);
            onRemoveCampaign(campaign.id, campaign.name);
            sendNotification(
                `Post removed from "${campaign.name}" campaign successfully`,
                { context: 'success' },
            );
        } catch (e: any) {
            sendNotification(e.message, { context: 'warning' });
            Sentry.captureException(e);
        }
    };

    const truncateName = (name: string) =>
        name.length > 7 ? `${name.slice(0, 7).trim()}..` : name;

    const renderTag = (campaign: Campaign) => (
        <Tooltip title={campaign.name} key={campaign.id}>
            <Tag closable onClose={handleRemove(campaign)}>
                {truncateName(campaign.name)}
            </Tag>
        </Tooltip>
    );

    const tagContainerStyle: React.CSSProperties = {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
    };

    if (showCollapse) {
        const extraCount =
            campaigns.length - MAX_CAMPAIGN_TAGS_SHOWN_IN_COLLAPSE;
        return (
            <div style={tagContainerStyle}>
                {campaigns
                    .slice(0, MAX_CAMPAIGN_TAGS_SHOWN_IN_COLLAPSE)
                    .map(renderTag)}
                <Tag
                    onClick={() => setCollapsed(false)}
                    style={{ cursor: 'pointer' }}
                >
                    {`+ ${extraCount} More`}
                </Tag>
            </div>
        );
    }

    return <div style={tagContainerStyle}>{campaigns.map(renderTag)}</div>;
};

export default connect(
    ['client'],
    combineActions({ sendNotification }),
)(ExpandableCampaignTags);
